import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, BookOpen, Search, CheckCircle, AlertCircle, Loader, ArrowRight, Type } from 'lucide-react';
import { verseService } from '../../../services/supabaseService';
import { bibleService } from '../../../services/bibleService';
import { supabase } from '../../../lib/supabase';

const VALID_BOOKS = [
  "Genesis","Exodus","Leviticus","Numbers","Deuteronomy","Joshua","Judges","Ruth",
  "1 Samuel","2 Samuel","1 Kings","2 Kings","1 Chronicles","2 Chronicles","Ezra",
  "Nehemiah","Esther","Job","Psalms","Psalm","Proverbs","Ecclesiastes","Song of Solomon",
  "Song of Songs","Isaiah","Jeremiah","Lamentations","Ezekiel","Daniel","Hosea","Joel",
  "Amos","Obadiah","Jonah","Micah","Nahum","Habakkuk","Zephaniah","Haggai","Zechariah",
  "Malachi","Matthew","Mark","Luke","John","Acts","Romans","1 Corinthians","2 Corinthians",
  "Galatians","Ephesians","Philippians","Colossians","1 Thessalonians","2 Thessalonians",
  "1 Timothy","2 Timothy","Titus","Philemon","Hebrews","James","1 Peter","2 Peter",
  "1 John","2 John","3 John","Jude","Revelation"
];

const BOOK_ABBREVS = {
  'gen':'Genesis','ex':'Exodus','exo':'Exodus','lev':'Leviticus','num':'Numbers',
  'deut':'Deuteronomy','deu':'Deuteronomy','josh':'Joshua','judg':'Judges','jdg':'Judges',
  'ruth':'Ruth','1sam':'1 Samuel','2sam':'2 Samuel','1kgs':'1 Kings','1ki':'1 Kings',
  '2kgs':'2 Kings','2ki':'2 Kings','1chr':'1 Chronicles','1ch':'1 Chronicles',
  '2chr':'2 Chronicles','2ch':'2 Chronicles','ezra':'Ezra','neh':'Nehemiah','est':'Esther',
  'esth':'Esther','job':'Job','ps':'Psalms','psa':'Psalms','pss':'Psalms','prov':'Proverbs',
  'pro':'Proverbs','eccl':'Ecclesiastes','ecc':'Ecclesiastes','song':'Song of Solomon',
  'sos':'Song of Solomon','isa':'Isaiah','jer':'Jeremiah','lam':'Lamentations',
  'ezek':'Ezekiel','eze':'Ezekiel','dan':'Daniel','hos':'Hosea','joel':'Joel','amos':'Amos',
  'obad':'Obadiah','jon':'Jonah','mic':'Micah','nah':'Nahum','hab':'Habakkuk',
  'zeph':'Zephaniah','zep':'Zephaniah','hag':'Haggai','zech':'Zechariah','zec':'Zechariah',
  'mal':'Malachi','matt':'Matthew','mat':'Matthew','mk':'Mark','mar':'Mark','lk':'Luke',
  'luk':'Luke','jn':'John','joh':'John','acts':'Acts','act':'Acts','rom':'Romans',
  '1cor':'1 Corinthians','1co':'1 Corinthians','2cor':'2 Corinthians','2co':'2 Corinthians',
  'gal':'Galatians','eph':'Ephesians','phil':'Philippians','php':'Philippians',
  'col':'Colossians','1thess':'1 Thessalonians','1th':'1 Thessalonians',
  '2thess':'2 Thessalonians','2th':'2 Thessalonians','1tim':'1 Timothy','1ti':'1 Timothy',
  '2tim':'2 Timothy','2ti':'2 Timothy','tit':'Titus','phlm':'Philemon','phm':'Philemon',
  'heb':'Hebrews','jas':'James','1pet':'1 Peter','1pe':'1 Peter','2pet':'2 Peter',
  '2pe':'2 Peter','1jn':'1 John','1jo':'1 John','2jn':'2 John','2jo':'2 John',
  '3jn':'3 John','3jo':'3 John','jude':'Jude','rev':'Revelation','re':'Revelation'
};

function normalizeReference(raw) {
  const trimmed = raw?.trim();
  if (!trimmed) return null;

  // Match: [optional number][book name] [chapter]:[verse start][-verse end]
  const match = trimmed?.match(/^(\d?\s?[A-Za-z]+(?:\s[A-Za-z]+)*)\s+(\d+):(\d+)(?:-(\d+))?$/);
  if (!match) return null;

  let [, bookRaw, chapter, verseStart, verseEnd] = match;
  bookRaw = bookRaw?.trim();

  // Try exact match first (case-insensitive)
  const exactMatch = VALID_BOOKS?.find(b => b?.toLowerCase() === bookRaw?.toLowerCase());
  if (exactMatch) {
    const bookName = exactMatch === 'Psalm' ? 'Psalms' : exactMatch;
    return {
      book: bookName,
      chapter: parseInt(chapter),
      verseStart: parseInt(verseStart),
      verseEnd: verseEnd ? parseInt(verseEnd) : null,
      reference: verseEnd
        ? `${bookName} ${chapter}:${verseStart}-${verseEnd}`
        : `${bookName} ${chapter}:${verseStart}`
    };
  }

  // Try abbreviation lookup
  const abbrevKey = bookRaw?.toLowerCase()?.replace(/\s+/g, '');
  const abbrevMatch = BOOK_ABBREVS?.[abbrevKey];
  if (abbrevMatch) {
    return {
      book: abbrevMatch,
      chapter: parseInt(chapter),
      verseStart: parseInt(verseStart),
      verseEnd: verseEnd ? parseInt(verseEnd) : null,
      reference: verseEnd
        ? `${abbrevMatch} ${chapter}:${verseStart}-${verseEnd}`
        : `${abbrevMatch} ${chapter}:${verseStart}`
    };
  }

  return null;
}

export default function AddVerseModal({ isOpen, onClose, onVerseAdded, userId }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null); // null | 'type' | 'reader'
  const [reference, setReference] = useState('');
  const [validationState, setValidationState] = useState('idle'); // idle | validating | valid | invalid | duplicate
  const [validatedVerse, setValidatedVerse] = useState(null);
  const [validationMessage, setValidationMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && mode === 'type') {
      setTimeout(() => inputRef?.current?.focus(), 100);
    }
  }, [isOpen, mode]);

  useEffect(() => {
    if (!isOpen) {
      setMode(null);
      setReference('');
      setValidationState('idle');
      setValidatedVerse(null);
      setValidationMessage('');
      setSaveError('');
    }
  }, [isOpen]);

  const validateReference = useCallback(async (raw) => {
    if (!raw?.trim()) {
      setValidationState('idle');
      setValidatedVerse(null);
      setValidationMessage('');
      return;
    }

    const parsed = normalizeReference(raw);
    if (!parsed) {
      setValidationState('invalid');
      setValidatedVerse(null);
      setValidationMessage('Format: Book Chapter:Verse (e.g. John 3:16 or Ps 23:1-3)');
      return;
    }

    setValidationState('validating');
    setValidationMessage('Checking verse...');

    try {
      // Use bibleService.getVerseByReference which queries bible_verses table directly
      const { data, error } = await bibleService?.getVerseByReference(
        parsed?.book,
        parsed?.chapter,
        parsed?.verseStart
      );

      if (error || !data) {
        setValidationState('invalid');
        setValidatedVerse(null);
        setValidationMessage(`"${parsed?.reference}" not found in the Bible database`);
        return;
      }

      // Check if already in user's library
      if (userId) {
        const { data: existing } = await supabase
          ?.from('verses')
          ?.select('id')
          ?.eq('user_id', userId)
          ?.ilike('reference', `${parsed?.book} ${parsed?.chapter}:${parsed?.verseStart}%`)
          ?.maybeSingle();

        if (existing) {
          setValidationState('duplicate');
          setValidatedVerse({ ...data, parsedRef: parsed });
          setValidationMessage('This verse is already in your library');
          return;
        }
      }

      setValidationState('valid');
      setValidatedVerse({ ...data, parsedRef: parsed });
      setValidationMessage(`Found: ${data?.reference || parsed?.reference}`);
    } catch (err) {
      setValidationState('invalid');
      setValidationMessage('Error checking verse. Please try again.');
    }
  }, [userId]);

  const handleReferenceChange = (e) => {
    const val = e?.target?.value;
    setReference(val);
    setValidationState('idle');
    setValidatedVerse(null);
    setValidationMessage('');

    if (debounceRef?.current) clearTimeout(debounceRef?.current);
    if (val?.trim()?.length > 3) {
      debounceRef.current = setTimeout(() => validateReference(val), 500);
    }
  };

  const handleAddVerse = async () => {
    if (validationState !== 'valid' || !validatedVerse || !userId) return;

    setIsSaving(true);
    setSaveError('');

    try {
      const parsed = validatedVerse?.parsedRef;

      // Build the display reference using the parsed range (e.g. "Psalms 37:2-3")
      const displayReference = parsed?.reference;

      // Build combined text for range verses
      let verseText = validatedVerse?.text || validatedVerse?.verse_text || '';

      // If it's a range, fetch all verses in the range and concatenate
      if (parsed?.verseEnd && parsed?.verseEnd !== parsed?.verseStart) {
        try {
          const rangeTexts = [];
          for (let v = parsed?.verseStart; v <= parsed?.verseEnd; v++) {
            const { data: vd } = await bibleService?.getVerseByReference(parsed?.book, parsed?.chapter, v);
            if (vd?.text) rangeTexts?.push(`[${v}] ${vd?.text}`);
          }
          if (rangeTexts?.length > 0) verseText = rangeTexts?.join(' ');
        } catch {
          // fall back to single verse text already fetched
        }
      }

      const { data, error } = await verseService?.createVerseFromBibleText(
        userId,
        parsed?.book,
        parsed?.chapter,
        parsed?.verseStart,
        parsed?.verseEnd || parsed?.verseStart,
        verseText,
        displayReference,
        'kjv'
      );

      if (error) {
        setSaveError(error?.message || 'Failed to add verse');
        return;
      }

      onVerseAdded?.();
      onClose();
    } catch (err) {
      setSaveError(err?.message || 'Failed to add verse');
    } finally {
      setIsSaving(false);
    }
  };

  const handleGoToReader = () => {
    onClose();
    navigate('/bible-reader');
  };

  if (!isOpen) return null;

  const getValidationIcon = () => {
    switch (validationState) {
      case 'validating': return <Loader className="w-4 h-4 text-primary animate-spin" />;
      case 'valid': return <CheckCircle className="w-4 h-4 text-success" />;
      case 'invalid': return <AlertCircle className="w-4 h-4 text-destructive" />;
      case 'duplicate': return <AlertCircle className="w-4 h-4 text-warning" />;
      default: return null;
    }
  };

  const getValidationColor = () => {
    switch (validationState) {
      case 'valid': return 'border-success focus:border-success focus:ring-success/30';
      case 'invalid': return 'border-destructive focus:border-destructive focus:ring-destructive/30';
      case 'duplicate': return 'border-warning focus:border-warning focus:ring-warning/30';
      case 'validating': return 'border-primary focus:border-primary focus:ring-primary/30';
      default: return 'border-border focus:border-primary focus:ring-primary/30';
    }
  };

  const getValidationTextColor = () => {
    switch (validationState) {
      case 'valid': return 'text-success';
      case 'invalid': return 'text-destructive';
      case 'duplicate': return 'text-warning';
      case 'validating': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative z-10 w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl animate-in zoom-in-95 fade-in duration-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 bg-primary/15 rounded-full flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-bold text-foreground">Add Verse</h2>
              <p className="text-xs text-muted-foreground">Add a verse to your library</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6">
          {/* Mode Selection */}
          {!mode && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground mb-4">How would you like to add a verse?</p>

              {/* Option 1: Type Reference */}
              <button
                onClick={() => setMode('type')}
                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-border bg-card hover:border-primary/50 hover:bg-primary/5 transition-all duration-200 group text-left"
              >
                <div className="h-11 w-11 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                  <Type className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-foreground">Type a Reference</p>
                  <p className="text-sm text-muted-foreground">Enter a verse reference like "John 3:16" or "Psalm 23:1-3"</p>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
              </button>

              {/* Option 2: Go to Reader */}
              <button
                onClick={handleGoToReader}
                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-border bg-card hover:border-primary/50 hover:bg-primary/5 transition-all duration-200 group text-left"
              >
                <div className="h-11 w-11 bg-success/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-success/20 transition-colors">
                  <Search className="w-5 h-5 text-success" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-foreground">Browse in Bible Reader</p>
                  <p className="text-sm text-muted-foreground">Navigate to the reader to find and add verses from any passage</p>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-success group-hover:translate-x-1 transition-all" />
              </button>
            </div>
          )}

          {/* Type Reference Mode */}
          {mode === 'type' && (
            <div className="space-y-4">
              <button
                onClick={() => { setMode(null); setReference(''); setValidationState('idle'); setValidatedVerse(null); setValidationMessage(''); }}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2"
              >
                <ArrowRight className="w-3 h-3 rotate-180" />
                Back
              </button>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Verse Reference
                </label>
                <div className="relative">
                  <input
                    ref={inputRef}
                    type="text"
                    value={reference}
                    onChange={handleReferenceChange}
                    placeholder="e.g. John 3:16 or Isa 41:10"
                    className={`w-full px-4 py-3 pr-10 rounded-xl border-2 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 transition-all duration-200 text-base ${getValidationColor()}`}
                  />
                  {validationState !== 'idle' && (
                    <div className="absolute inset-y-0 right-3 flex items-center">
                      {getValidationIcon()}
                    </div>
                  )}
                </div>

                {/* Validation message */}
                {validationMessage && (
                  <p className={`mt-2 text-sm flex items-center gap-1.5 ${getValidationTextColor()}`}>
                    {getValidationIcon()}
                    {validationMessage}
                  </p>
                )}

                {/* Hint */}
                {validationState === 'idle' && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Supports full names (John, Psalms) and abbreviations (Jn, Ps, Isa, 1Cor)
                  </p>
                )}
              </div>

              {/* Verse Preview */}
              {validationState === 'valid' && validatedVerse && (
                <div className="bg-success/5 border border-success/20 rounded-xl p-4">
                  <p className="text-xs font-semibold text-success mb-1 uppercase tracking-wide">Verse Found</p>
                  <p className="text-sm font-medium text-foreground mb-1">
                    {validatedVerse?.reference || validatedVerse?.parsedRef?.reference}
                  </p>
                  <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                    {validatedVerse?.text || validatedVerse?.verse_text}
                  </p>
                </div>
              )}

              {/* Save Error */}
              {saveError && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0" />
                  <p className="text-sm text-destructive">{saveError}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-border text-foreground text-sm font-medium hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddVerse}
                  disabled={validationState !== 'valid' || isSaving}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <BookOpen className="w-4 h-4" />
                      Add to Library
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
